import * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import { saveAs } from 'file-saver';
import { map, Observable, tap } from 'rxjs';

import { inject, Injectable } from '@angular/core';

import { ExportableData, ExportOptions, LogActionEnum } from '../../models';
import { AppLogsService } from './app-logs.service';

@Injectable({
  providedIn: 'root',
})
export class ExportService<T> {
  private readonly appLogsService = inject(AppLogsService);

  export<T>(options: {
    getData: () => Observable<T[]>;
    mapData: (data: T[]) => ExportableData[];
    exportOptions: ExportOptions;
  }): Observable<void> {
    const { exportUrl, exportEntity } = options.exportOptions;

    return options.getData().pipe(
      tap(() => {
        this.appLogsService.create({
          action: LogActionEnum.EXPORT,
          url: exportUrl,
          entity: exportEntity,
        });
      }),
      tap((data) => {
        const mapped = options.mapData(data).map((item) => {
          return Object.keys(item).reduce((acc, key) => {
            acc[key] = item[key].value || '';
            return acc;
          }, {});
        });

        this.exportArrayToXlsx(mapped, 'grid');
      }),
      map(() => void 0)
    );
  }

  async exportArrayToXlsx(data: any[], fileName: string): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    const headerRow = worksheet.addRow(Object.keys(data[0]).map((key) => key));

    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1F4E78' },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    data.forEach((row) => {
      worksheet.addRow(
        Object.keys(row).map((key) => row[key].value || row[key])
      );
    });

    worksheet.columns = Object.keys(data[0]).map((key) => ({
      header: key,
      key: key,
      width: data[0][key]?.exportWidth || 20,
    }));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, `${fileName}.xlsx`);
  }
}
